import { AgentResponse, AgentToolCallSegment } from '../../utils/agent-client';

// "Why this plan" telemetry: the agent's own structured reasoning, captured LIVE
// from the plan-submission tool call the planner makes. Nothing here is authored
// or post-hoc — every field is read straight from the arguments the live agent
// passed (per-line rationale and the networks it explicitly rejected, with
// reasons). If the shape ever drifts, extraction returns undefined and the panel
// simply shows nothing rather than inventing a rationale.

export interface PlanLineItem {
  network: string;
  budget: number;
  cpm: number;
  dsp: string;
  channel: string;
  rationale: string;
  isDirectIo: boolean;
  // The specific audience/segment this line targets (from the agent's per-line
  // selection); '' for a deliberate broad/RON reach line.
  audienceSegment: string;
}

export interface ExcludedOption {
  name: string;
  reason: string;
}

export interface CapturedPlan {
  planSummary: string;
  audienceStrategy: string;
  geoStrategy: string;
  primaryKpi: string;
  lineItems: PlanLineItem[];
  excludedOptions: ExcludedOption[];
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const num = (value: unknown): number => (typeof value === 'number' ? value : 0);
const arr = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const toLineItem = (raw: unknown): PlanLineItem => {
  const r = asRecord(raw);
  return {
    network: str(r.network_name) || str(r.network),
    budget: num(r.booked_gross_media),
    cpm: num(r.cpm),
    dsp: str(r.dsp),
    channel: str(r.channel),
    rationale: str(r.rationale) || str(r.description),
    isDirectIo: r.is_direct_io === true,
    audienceSegment: str(r.audience_segment)
  };
};

const toExcludedOption = (raw: unknown): ExcludedOption => {
  const r = asRecord(raw);
  return { name: str(r.name), reason: str(r.reason) };
};

// Only the media-planning agent's plan-submission tools carry the structured
// plan. Both names are accepted (submit_plan wraps render_media_plan_xlsx) so a
// flow that calls either is captured — without treating an unrelated tool call
// that merely happens to include a line_items array as "the plan".
const PLAN_TOOL_NAMES = new Set(['submit_plan', 'render_media_plan_xlsx']);

const buildPlan = (a: Record<string, unknown>): CapturedPlan => ({
  planSummary: str(a.plan_summary),
  audienceStrategy: str(a.audience_strategy),
  geoStrategy: str(a.geo_strategy),
  primaryKpi: str(a.primary_kpi),
  lineItems: arr(a.line_items).map(toLineItem),
  excludedOptions: arr(a.excluded_options).map(toExcludedOption)
});

export const extractCapturedPlan = (response: AgentResponse): CapturedPlan | undefined =>
  response.segments
    .filter(
      (segment): segment is AgentToolCallSegment =>
        segment.type === 'tool_call' && PLAN_TOOL_NAMES.has(segment.name)
    )
    .map(segment => buildPlan(segment.args))
    .find(candidate => candidate.lineItems.length > 0);
